import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody, CardHeader } from '../components/Card.js'
import { Empty } from '../components/Empty.js'
import { Money } from '../components/Money.js'

export const Route = createFileRoute('/_app/super/health')({
  component: SystemHealthPage,
})

interface HealthPayload {
  notifications: { failedLast7d: number }
  payments: { failedTotal: number }
  biometric: {
    recent: {
      id: string
      academyId: string
      syncedAt: string
      status: string
      recordCount: number | null
      error: string | null
    }[]
  }
}

interface RevenuePayload {
  mrrInr: number
  arrInr: number
  lifetimePaidInr: number
  pendingInr: number
  refundedInr: number
  failedInvoices: number
  academies: Record<string, number>
}

function SystemHealthPage() {
  const health = useQuery({
    queryKey: ['super', 'health'],
    queryFn: async () => {
      const res = await api<HealthPayload>('/api/v1/super/system-health')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })
  const revenue = useQuery({
    queryKey: ['super', 'revenue'],
    queryFn: async () => {
      const res = await api<RevenuePayload>('/api/v1/super/revenue')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="System health"
        description="Platform-wide signals from the last 7 days. Use this to triage incidents."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile
          label="MRR (last 30 days)"
          value={revenue.data ? <Money paise={revenue.data.mrrInr} /> : '—'}
          tone="success"
        />
        <Tile
          label="ARR projection"
          value={revenue.data ? <Money paise={revenue.data.arrInr} /> : '—'}
        />
        <Tile
          label="Pending invoices"
          value={revenue.data ? <Money paise={revenue.data.pendingInr} /> : '—'}
          tone="warning"
        />
        <Tile
          label="Refunded"
          value={revenue.data ? <Money paise={revenue.data.refundedInr} /> : '—'}
        />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile
          label="Notification failures (7d)"
          value={health.data ? String(health.data.notifications.failedLast7d) : '—'}
          tone={
            health.data && health.data.notifications.failedLast7d > 0
              ? 'danger'
              : 'success'
          }
        />
        <Tile
          label="Failed invoices"
          value={health.data ? String(health.data.payments.failedTotal) : '—'}
          tone={
            health.data && health.data.payments.failedTotal > 0 ? 'danger' : 'success'
          }
        />
        <Tile
          label="Active academies"
          value={revenue.data ? String(revenue.data.academies.active ?? 0) : '—'}
          tone="success"
        />
        <Tile
          label="On trial"
          value={revenue.data ? String(revenue.data.academies.trial ?? 0) : '—'}
          tone="warning"
        />
      </div>

      <Card>
        <CardHeader
          title="Recent biometric syncs"
          description="Last 10 device pushes — failures here usually mean a misconfigured key."
        />
        {health.data && health.data.biometric.recent.length === 0 ? (
          <Empty
            icon="inbox"
            title="No biometric syncs yet"
            description="Devices will start showing up here once an academy connects one."
          />
        ) : (
          <ul className="divide-y divide-border">
            {health.data?.biometric.recent.map((row) => {
              const tone =
                row.status === 'ok'
                  ? 'border-success-border bg-success-tint text-success'
                  : row.status === 'partial'
                    ? 'border-warning-border bg-warning-tint text-warning'
                    : 'border-danger-border bg-danger-tint text-danger'
              return (
                <li key={row.id} className="px-5 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-mono text-fg">{row.academyId}</p>
                      <p className="text-xs text-fg-subtle">
                        {new Date(row.syncedAt).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                        {row.recordCount !== null
                          ? ` · ${row.recordCount} records`
                          : ''}
                      </p>
                      {row.error ? (
                        <p className="mt-1 text-xs text-danger break-words">
                          {row.error}
                        </p>
                      ) : null}
                    </div>
                    <span
                      className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}
                    >
                      {row.status}
                    </span>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </Card>
    </div>
  )
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string
  value: React.ReactNode
  tone?: 'success' | 'warning' | 'danger'
}) {
  const valueCls =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : tone === 'danger'
          ? 'text-danger'
          : 'text-fg'
  return (
    <Card>
      <CardBody>
        <p className="text-xs text-fg-subtle">{label}</p>
        <p className={`mt-1 text-2xl font-semibold tabular-nums ${valueCls}`}>{value}</p>
      </CardBody>
    </Card>
  )
}
